/* eslint-disable @mizdra/layout-shift/require-size-attributes */
import React, { useEffect } from 'react';
import classNames from 'classnames/bind';
import { bool, number, string, shape as shapeType } from 'prop-types';
import {
  arrayOf,
  customType,
  fragment,
  params,
  shape,
  string as stringType,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { useImpression } from '@thd-olt-component-react/impression';
import {
  Button,
  Typography,
  Card,
  CardBody,
  CardMedia,
  CardTitle,
} from '@one-thd/sui-atomic-components';
import { SpotlightB2b } from './SpotlightB2b';
import { publish } from './utils/publisher';
import VideoPlayerWrap from './VideoPlayerWrap';
import styles from './spotlight.scss';

const Spotlight = ({
  componentId,
  componentClass,
  componentPosition,
  showDescription,
  useB2bStyles,
  proAnalyticsData,
}) => {
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('spotlight.ready');
  }, []);

  const cx = classNames.bind(styles);

  const { data } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass,
    },
  });

  const impressionProviderContextData = useImpression({
    data: {
      id: componentId,
      name: Spotlight.displayName,
      component: Spotlight.displayName,
      position: componentPosition,
      type: 'content',
    },
  });

  const dataComponent = data?.component;

  let {
    cta, description, link, title, altText, proAnalyticsCampaign, proAnalyticsComponent,
    videoUrl
  } = dataComponent || {};

  const {
    image: dataComponentImage,
    previewImage: dataComponentPreviewImage,
  } = dataComponent || {};

  // During local development, Contentful returns "String" instead of "",
  //    undefined or null, when the video URL field is empty.
  // We're not sure why, but without this check the component doesn't behave as expected.
  if (/string/i.test(videoUrl)) {
    videoUrl = null;
  }

  let { url: imageUrl } = dataComponentImage?.damDownloadedContent || {};

  if (dataComponentPreviewImage) {
    const damDownloadedContentImage = dataComponentPreviewImage.damDownloadedContent;
    const damContentSelectorImage = dataComponentPreviewImage.damContentSelector?.assetData?.[0];

    if (damDownloadedContentImage?.url) {
      imageUrl = damDownloadedContentImage?.url;
    } else {
      imageUrl = damContentSelectorImage?.selectedImageUrl || imageUrl;
    }
  }

  const akamaiImageResize = (imageUrlToTransform) => {
    const queryParams = 'Resize=(703,395.44)';

    if (!imageUrlToTransform) {
      return imageUrlToTransform;
    }
    const isAkamaiHost = imageUrlToTransform.includes('thdstatic.com');
    if (!isAkamaiHost) {
      return imageUrlToTransform;
    }
    if (!imageUrlToTransform.includes('?')) {
      return `${imageUrlToTransform}?im=${queryParams}`;
    }
    if (imageUrlToTransform.includes('im=')) {
      const urlBeforeImParams = imageUrlToTransform.substring(
        0,
        imageUrlToTransform.indexOf('im=')
      );
      const imAndRemainderParams = imageUrlToTransform.substring(
        imageUrlToTransform.indexOf('im=')
      );
      const hasAdditionalParams = imAndRemainderParams.includes('&');
      if (hasAdditionalParams) {
        const remainderOfParams = imAndRemainderParams.substring(imAndRemainderParams.indexOf('&'));
        const imParams = imAndRemainderParams.substring(0, imAndRemainderParams.indexOf('&'));
        return `${urlBeforeImParams}${imParams};${queryParams}${remainderOfParams}`;
      }
      const imParams = imageUrlToTransform.substring(imageUrlToTransform.indexOf('im='));
      return `${urlBeforeImParams}${imParams};${queryParams}`;
    }
    return `${imageUrlToTransform}&im=${queryParams}`;
  };

  imageUrl = akamaiImageResize(imageUrl);

  const handleContentClick = () => {
    if (proAnalyticsData.isB2B) {
      // Input Data for widget should be collected from component data
      publish({
        widgetId: componentId,
        widgetTemplates: proAnalyticsData.templates,
        widgetCampaign: proAnalyticsCampaign,
        widgetComponent: proAnalyticsComponent,
      });
    }
  };
  if (useB2bStyles) {
    return (
      <SpotlightB2b
        componentId={componentId}
        componentClass={componentClass}
        componentPosition={componentPosition}
        proAnalyticsData={proAnalyticsData}
      />
    );
  }

  return (
    <div
      data-component-id={componentId}
      id={`${componentClass}-${componentPosition}-${componentId}`}
      data-component="Spotlight"
      data-component-name="Spotlight"
      data-component-position={componentPosition}
      // eslint-disable-next-line tailwindcss/no-custom-classname
      className="spotlight--grid sui-grid sui-h-full"
      ref={impressionProviderContextData.ref}
      // eslint-disable-next-line react/no-unknown-property
      clickid={impressionProviderContextData.clickID}
    >
      <Card className={cx('spotlight-card__link--hover-no-underline spotlight-card')}>
        {(videoUrl) ? (
          <VideoPlayerWrap videoUrl={videoUrl} />
        ) : (
          <CardMedia
            src={imageUrl}
            aspect="wide"
            alt={altText || title}
            title={`Image for ${title}`}
          />
        )}
        <CardTitle header={title} />
        <CardBody>
          {showDescription && description && (
            <Typography height="normal" weight="regular" variant="body-base" color="primary">
              {description}
            </Typography>
          )}
        </CardBody>
        <Button href={link} variant="secondary" onClick={handleContentClick}>
          {cta || ''}
        </Button>
      </Card>
    </div>
  );
};

Spotlight.propTypes = {
  componentId: string.isRequired,
  componentPosition: number,
  componentClass: string,
  showDescription: bool,
  useB2bStyles: bool,
  proAnalyticsData: shapeType({
    isB2B: bool,
    templates: string,
  }),
};

Spotlight.defaultProps = {
  componentPosition: 1,
  componentClass: '',
  showDescription: false,
  useB2bStyles: false,
  proAnalyticsData: {},
};

Spotlight.displayName = 'Spotlight';

Spotlight.dataModel = {
  component: params({
    id: stringType().isRequired(),
    componentClass: customType('ComponentClass').enum(['Spotlight']).isRequired(),
  }).shape({
    Spotlight: fragment().shape({
      id: stringType(),
      title: stringType(),
      altText: stringType(),
      cta: stringType(),
      link: stringType(),
      componentName: stringType(),
      description: stringType(),
      proAnalyticsCampaign: stringType(),
      proAnalyticsComponent: stringType(),
      videoUrl: stringType(),
      image: shape({
        damDownloadedContent: shape({
          url: stringType(),
        }),
      }),
      previewImage: shape({
        damContentSelector: shape({
          assetData: arrayOf(
            shape({
              selectedImageUrl: stringType(),
            })
          ),
        }),
        damDownloadedContent: shape({
          url: stringType(),
        }),
      }),
    }),
  }),
};

export { Spotlight };
